/**
 * What "Add" means when somebody presses it on the invitee panel.
 *
 * Enter and the Add button were two different features. Enter added a typed
 * address, or else confirmed the contacts ticked in the dropdown; Add only ever
 * did the first, so ticking names and pressing Add did nothing at all. And
 * neither would add a typed address the directory already knew — the panel
 * hides its "Invite <address>" row for a known contact, on the reasoning that
 * you should tick them instead, which left Enter with nothing to do for exactly
 * the addresses most likely to be typed: colleagues on the company domain.
 *
 * Both buttons ask this now, so they cannot drift apart again, and a typed
 * address counts whether or not the directory has heard of it.
 */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export const isEmailAddress = (value: string): boolean => EMAIL_RE.test(value.trim())

export type AddInviteesPlan =
  /** A typed address, ready to go in. */
  | { action: 'add-typed'; email: string }
  /** Nothing typed worth adding, but names are ticked. */
  | { action: 'confirm-ticked' }
  /** Something was typed and it is not an address. Say so rather than sit there. */
  | { action: 'reject'; reason: 'not-an-address'; text: string }
  /**
   * Already on the list.
   *
   * Merging quietly dropped them, which is the right thing to DO and the wrong
   * thing to say nothing about: pressing Add and watching the box empty with
   * the list unchanged reads as the panel having missed the click.
   */
  | { action: 'reject'; reason: 'already-invited'; text: string }
  /** More people than the meeting has room for. */
  | { action: 'reject'; reason: 'seats-exceeded'; seats: number; wanted: number }
  /** An empty box and nothing ticked — the press meant nothing. */
  | { action: 'none' }

const sameMembers = (a: string[], b: string[]): boolean => {
  if (a.length !== b.length) return false
  const bSet = new Set(b)
  return a.every((v) => bSet.has(v))
}

export function planInviteeAdd(input: {
  /** Whatever is in the search box. */
  typed: string
  /** The contacts ticked in the dropdown. */
  ticked: string[]
  /**
   * The tick state as it stood when the dropdown opened. Ticking seeds itself
   * from the invitee list, so `ticked` is rarely actually empty — without this,
   * that seeded-but-untouched baseline reads exactly like a deliberate tick,
   * and typed text that is not an address (e.g. a name typed to filter the
   * dropdown, never followed by a tick) silently confirms the unchanged list
   * instead of being rejected. Omit it and any non-empty `ticked` counts as
   * changed, matching the old behaviour.
   */
  baseline?: string[]
  /** Who is on the list already — the set this add would merge into. */
  invited?: string[]
  /** How many people the meeting has room for, the organiser included. */
  seats?: number
}): AddInviteesPlan {
  const typed = input.typed.trim()
  const invited = input.invited ?? []
  const { seats } = input

  // A typed address wins over the ticks rather than cancelling them: adding it
  // merges into the ticked set, so both land together.
  if (typed && isEmailAddress(typed)) {
    if (invited.some((e) => e.trim().toLowerCase() === typed.toLowerCase())) {
      return { action: 'reject', reason: 'already-invited', text: typed }
    }
    if (seats != null && invited.length + 1 > seats) {
      return { action: 'reject', reason: 'seats-exceeded', seats, wanted: invited.length + 1 }
    }
    return { action: 'add-typed', email: typed }
  }

  const tickedChanged = !sameMembers(input.ticked, input.baseline ?? [])
  if (input.ticked.length > 0 && tickedChanged) {
    // The ticked set REPLACES the list rather than adding to it — it was
    // seeded from the list, so unticking has to remove — which makes its own
    // size the number of people the meeting would end up with.
    if (seats != null && input.ticked.length > seats) {
      return { action: 'reject', reason: 'seats-exceeded', seats, wanted: input.ticked.length }
    }
    return { action: 'confirm-ticked' }
  }

  if (typed) return { action: 'reject', reason: 'not-an-address', text: typed }
  return { action: 'none' }
}

/**
 * Append addresses to a list, ignoring the ones already on it.
 *
 * Case-insensitively: the directory stores what a person typed when they signed
 * up, and somebody inviting `John@instavc.com` after `john@instavc.com` was
 * already in the list added them a second time — two rows, two invitations, one
 * person.
 */
export function mergeEmails(base: string[], additions: string[]): {
  merged: string[]
  /** Only the ones that were not already there, in the case they were typed. */
  added: string[]
} {
  const seen = new Set(base.map((e) => e.trim().toLowerCase()))
  const merged = [...base]
  const added: string[] = []

  for (const raw of additions) {
    const email = raw.trim()
    const key = email.toLowerCase()
    if (!email || seen.has(key)) continue
    seen.add(key)
    merged.push(email)
    added.push(email)
  }

  return { merged, added }
}
